/**
 * Which codec this browser will actually encode with, asked rather than assumed.
 *
 * The first version of this file hardcoded `avc1.42001f`, H.264 baseline, on the
 * reasoning that the most conservative profile is the most widely supported. A
 * capability probe on a stock Chrome build disagreed: baseline was refused and
 * main profile accepted. AAC was refused outright while Opus was fine. Both are
 * ordinary configurations on an ordinary browser, so the only safe approach is
 * to ask for each candidate in preference order and use the first that answers.
 *
 * Order is compatibility first. An H.264 MP4 plays everywhere a person is
 * likely to send it; VP9 and AV1 are excellent and smaller but a phone's stock
 * gallery app may refuse them, so they are a fallback rather than a default.
 */

/** Container track codec, as mp4-muxer names it. */
export type MuxVideoCodec = "avc" | "hevc" | "vp9" | "av1";
export type MuxAudioCodec = "aac" | "opus";

export interface VideoChoice {
  /** WebCodecs codec string. */
  codec: string;
  mux: MuxVideoCodec;
  label: string;
  /** False when a phone's stock player may refuse the result. */
  portable: boolean;
}

export interface AudioChoice {
  codec: string;
  mux: MuxAudioCodec;
  label: string;
}

const VIDEO_CANDIDATES: VideoChoice[] = [
  { codec: "avc1.4d0028", mux: "avc", label: "H.264 main", portable: true },
  { codec: "avc1.42001f", mux: "avc", label: "H.264 baseline", portable: true },
  { codec: "avc1.640028", mux: "avc", label: "H.264 high", portable: true },
  { codec: "vp09.00.10.08", mux: "vp9", label: "VP9", portable: false },
  { codec: "av01.0.04M.08", mux: "av1", label: "AV1", portable: false },
];

const AUDIO_CANDIDATES: AudioChoice[] = [
  { codec: "mp4a.40.2", mux: "aac", label: "AAC" },
  { codec: "opus", mux: "opus", label: "Opus" },
];

export interface EncodeSupport {
  video: (VideoChoice & { config: VideoEncoderConfig }) | null;
  audio: AudioChoice | null;
}

/**
 * Probing costs a handful of async calls, and the answer depends on the output
 * size, so it is asked per export rather than cached at load.
 */
export async function negotiate(width: number, height: number, fps: number): Promise<EncodeSupport> {
  return { video: await pickVideo(width, height, fps), audio: await pickAudio() };
}

async function pickVideo(
  width: number,
  height: number,
  framerate: number,
): Promise<(VideoChoice & { config: VideoEncoderConfig }) | null> {
  if (typeof VideoEncoder === "undefined") return null;
  for (const candidate of VIDEO_CANDIDATES) {
    const config: VideoEncoderConfig = {
      codec: candidate.codec,
      width,
      height,
      bitrate: bitrateFor(width, height),
      framerate,
      latencyMode: "quality",
    };
    try {
      const support = await VideoEncoder.isConfigSupported(config);
      // A browser may hand back a config it has adjusted; using its version
      // rather than ours avoids configuring with something it just declined.
      if (support.supported) return { ...candidate, config: support.config ?? config };
    } catch {
      continue;
    }
  }
  return null;
}

export async function pickAudio(): Promise<AudioChoice | null> {
  if (typeof AudioEncoder === "undefined") return null;
  for (const candidate of AUDIO_CANDIDATES) {
    try {
      const support = await AudioEncoder.isConfigSupported({
        codec: candidate.codec,
        sampleRate: 48000,
        numberOfChannels: 2,
        bitrate: 128_000,
      });
      if (support.supported) return candidate;
    } catch {
      continue;
    }
  }
  return null;
}

/** H.264 at a rate that looks right without producing files nobody can send. */
export function bitrateFor(width: number, height: number): number {
  const pixels = width * height;
  if (pixels >= 1920 * 1080) return 8_000_000;
  if (pixels >= 1280 * 720) return 5_000_000;
  return 2_500_000;
}

/** What to warn about once we know what we are going to produce. */
export function supportNote(support: EncodeSupport): string | null {
  if (!support.video) {
    return "This browser cannot encode video, so exporting won't work here. Chrome, Edge or Safari can.";
  }
  if (!support.audio) {
    return "This browser cannot encode audio, so the export will have no sound.";
  }
  if (!support.video.portable) {
    return `This browser has no H.264 encoder, so the export uses ${support.video.label}. It plays in browsers but some phone galleries and older players will refuse it.`;
  }
  return null;
}
